import { Store } from '@tanstack/store';

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'theme';
const DEFAULT_THEME: Theme = 'dark';

interface ThemeStoreState {
  theme: Theme;
}

function loadFromStorage(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    return DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

function getSystemTheme(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyToDOM(theme: Theme): void {
  const resolved = theme === 'system' ? getSystemTheme() : theme;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
}

export const themeStore = new Store<ThemeStoreState>({
  theme: loadFromStorage(),
});

// Apply theme on initial load
applyToDOM(themeStore.state.theme);

// ── localStorage persistence + DOM sync ──────────────────────────────

let fromStorageEvent = false;

themeStore.subscribe(() => {
  const { theme } = themeStore.state;
  applyToDOM(theme);

  if (fromStorageEvent) return;
  localStorage.setItem(STORAGE_KEY, theme);
});

// ── Multi-tab sync ───────────────────────────────────────────────────

window.addEventListener('storage', (e) => {
  if (e.key !== STORAGE_KEY) return;

  fromStorageEvent = true;
  const value = e.newValue as Theme | null;
  if (value === 'light' || value === 'dark' || value === 'system') {
    themeStore.setState((prev) => ({ ...prev, theme: value }));
  }
  fromStorageEvent = false;
});

// ── Listen for system theme changes when in 'system' mode ────────────

window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
  if (themeStore.state.theme === 'system') {
    applyToDOM('system');
  }
});

// ── Exported actions ─────────────────────────────────────────────────

export function setTheme(theme: Theme): void {
  themeStore.setState((prev) => ({ ...prev, theme }));
}

export function getTheme(): Theme {
  return themeStore.state.theme;
}
